import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, Sparkles, Smartphone } from "lucide-react";
import PageShell from "../components/PageShell";
import { usePremium } from "../context/PremiumContext";
import { SKUS, purchase } from "../lib/playBilling";
import { toast } from "sonner";

const PERKS = [
  "AI Bible Study notes for every chapter",
  "Personalized daily devotionals",
  "Guided reading plans",
  "Family Sharing & shared prayer wall",
  "Offline study downloads",
  "Everything in the free reader, always",
];

const FALLBACK = { [SKUS.monthly]: "$3.99", [SKUS.annual]: "$29.99" };

function priceFor(products, sku) {
  const p = products.find((x) => x.itemId === sku);
  if (p?.price?.value) {
    const cur = p.price.currency === "USD" ? "$" : `${p.price.currency} `;
    return `${cur}${p.price.value}`;
  }
  return FALLBACK[sku];
}

// Percent saved on the annual plan vs paying monthly for a year.
function annualSavings(products) {
  const num = (s) => parseFloat(String(s).replace(/[^0-9.]/g, ""));
  const monthly = num(priceFor(products, SKUS.monthly));
  const annual = num(priceFor(products, SKUS.annual));
  if (!monthly || !annual) return 0;
  const pct = Math.round((1 - annual / (monthly * 12)) * 100);
  return pct > 0 ? pct : 0;
}

export default function Premium() {
  const navigate = useNavigate();
  const { inApp, subscribed, products, refresh } = usePremium();
  const [busy, setBusy] = useState("");

  const onSubscribe = async (sku) => {
    if (!inApp) {
      toast("Subscriptions open in the Clarity Bible app — web checkout is coming soon.");
      return;
    }
    setBusy(sku);
    try {
      await purchase(sku);
      toast.success("Welcome to Premium! 🎉");
      await refresh();
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Subscription didn’t complete. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const perksCard = (
    <div className="rounded-3xl border border-border bg-background/40 p-7 backdrop-blur-md">
      <p className="font-ui mb-4 flex items-center gap-2 text-sm font-semibold">
        <Sparkles className="h-4 w-4 text-primary" /> Everything in Premium
      </p>
      <ul className="space-y-3">
        {PERKS.map((p, i) => (
          <li key={i} className="font-ui flex items-start gap-3 text-sm text-foreground/90">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {p}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <PageShell title="Clarity Bible Premium" subtitle="Go deeper in God’s Word" icon={Crown}
      accent={{ a: "#C8A96A", b: "#7A8450", c: "#3E5641" }} testid="premium-page">

      {/* WEB visitor — everything is free on the web */}
      {false && !inApp && (
        <div className="space-y-6" data-testid="premium-web" />
      )}

      {/* APP — already subscribed */}
      {inApp && subscribed && (
        <div className="rounded-3xl border border-primary/40 bg-primary/5 p-8 text-center backdrop-blur-md" data-testid="premium-active">
          <Crown className="mx-auto mb-3 h-8 w-8 text-primary" />
          <h2 className="font-serif-display text-2xl">You’re a Premium member</h2>
          <p className="font-ui mt-2 text-sm text-muted-foreground">Thank you for supporting Clarity Bible.</p>
          <button onClick={() => navigate("/")} className="font-ui mt-6 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
            Back to reading
          </button>
        </div>
      )}

      {/* Not subscribed — show the trial + plans (web and app) */}
      {!subscribed && (
        <>
          <div className="mb-8 rounded-2xl border border-border bg-background/40 p-5 text-center backdrop-blur-md" data-testid="trial-status">
            <p className="font-ui text-sm">Everything is unlocked <span className="font-bold text-primary">free for 3 days</span>. After that, choose the plan that fits — cancel anytime.</p>
          </div>

          <div className="mb-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-border bg-background/40 p-7 backdrop-blur-md">
              <p className="font-ui text-sm font-semibold text-muted-foreground">Monthly</p>
              <p className="font-serif-display mt-2 text-4xl">{priceFor(products, SKUS.monthly)}<span className="font-ui text-base text-muted-foreground">/mo</span></p>
              <button onClick={() => onSubscribe(SKUS.monthly)} disabled={!!busy} data-testid="subscribe-monthly"
                className="font-ui mt-6 w-full rounded-full border border-border py-3 text-sm font-semibold transition-colors hover:bg-muted disabled:opacity-50">
                {busy === SKUS.monthly ? "Starting…" : "Choose monthly"}
              </button>
            </div>
            <div className="relative rounded-3xl border-2 border-primary bg-primary/5 p-7 backdrop-blur-md">
              {annualSavings(products) > 0 && (
                <span className="font-ui absolute -top-3 left-7 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">Best value · save {annualSavings(products)}%</span>
              )}
              <p className="font-ui text-sm font-semibold text-primary">Annual</p>
              <p className="font-serif-display mt-2 text-4xl">{priceFor(products, SKUS.annual)}<span className="font-ui text-base text-muted-foreground">/yr</span></p>
              <button onClick={() => onSubscribe(SKUS.annual)} disabled={!!busy} data-testid="subscribe-annual"
                className="font-ui mt-6 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-50">
                {busy === SKUS.annual ? "Starting…" : "Start free trial"}
              </button>
            </div>
          </div>

          {perksCard}

          <p className="font-ui mt-6 flex items-center justify-center gap-2 text-center text-xs text-muted-foreground">
            <Smartphone className="h-3.5 w-3.5" /> Billed securely through Google Play. Cancel anytime.
          </p>
          <p className="font-ui mx-auto mt-3 max-w-sm text-center text-[11px] leading-relaxed text-muted-foreground/70" data-testid="free-translations-note">
            NLT, KJV, WEB and ASV — and the full Bible reader — stay free, no plan needed.
          </p>
        </>
      )}
    </PageShell>
  );
}
